import { useMemo } from 'react';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    AreaChart,
    ComposedChart,
    ReferenceLine
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

// Nomi mesi italiani
const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MONTHS_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Colori grafici
const COLORS = {
    production: '#f59e0b',  // amber
    consumption: '#ef4444', // red
    selfConsumption: '#22c55e', // green
    gridExport: '#3b82f6',  // blue
    gridImport: '#8b5cf6',  // purple
    savings: '#10b981',     // emerald
    cost: '#f43f5e',        // rose
    investment: '#6366f1',  // indigo
    roi: '#14b8a6',         // teal
};

/**
 * Grafico produzione mensile
 */
export function MonthlyProductionChart({ monthlyProduction, annualConsumption }) {
    const data = useMemo(() => {
        if (!monthlyProduction || monthlyProduction.length !== 12) return [];
        
        // Distribuzione mensile consumi tipica (residenziale Italia)
        const consumptionDistribution = [9.5, 8.5, 8.0, 7.5, 7.0, 7.5, 8.0, 8.5, 8.0, 8.5, 9.0, 10.0];
        const totalDist = consumptionDistribution.reduce((a, b) => a + b, 0);
        
        return MONTHS.map((month, i) => ({
            name: month,
            fullName: MONTHS_FULL[i],
            produzione: Math.round(monthlyProduction[i]),
            consumo: annualConsumption ? Math.round(annualConsumption * (consumptionDistribution[i] / totalDist)) : 0,
        }));
    }, [monthlyProduction, annualConsumption]);

    if (data.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Produzione vs Consumo Mensile</CardTitle>
                <CardDescription>Confronto tra energia prodotta e consumata</CardDescription>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis 
                            tick={{ fontSize: 12 }} 
                            tickFormatter={(v) => `${v}`}
                            label={{ value: 'kWh', angle: -90, position: 'insideLeft', fontSize: 12 }}
                        />
                        <Tooltip 
                            formatter={(value, name) => [
                                `${value.toLocaleString('it-IT')} kWh`,
                                name === 'produzione' ? 'Produzione FV' : 'Consumo'
                            ]}
                            labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                        />
                        <Legend 
                            formatter={(value) => value === 'produzione' ? 'Produzione FV' : 'Consumo'}
                        />
                        <Bar dataKey="produzione" fill={COLORS.production} radius={[4, 4, 0, 0]} />
                        <Line 
                            type="monotone" 
                            dataKey="consumo" 
                            stroke={COLORS.consumption} 
                            strokeWidth={2}
                            dot={{ fill: COLORS.consumption, r: 4 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

/**
 * Grafico flussi energetici
 */
export function EnergyFlowsChart({ simulation }) {
    const data = useMemo(() => {
        if (!simulation?.monthly_production) return [];
        
        const { monthly_production, self_consumption_percent = 70 } = simulation;
        
        return MONTHS.map((month, i) => {
            const prod = monthly_production[i] || 0;
            const selfCons = prod * (self_consumption_percent / 100);
            const exported = prod - selfCons;
            
            return {
                name: month,
                autoconsumo: Math.round(selfCons),
                immissione: Math.round(exported),
            };
        });
    }, [simulation]);

    if (data.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Flussi Energetici Mensili</CardTitle>
                <CardDescription>Autoconsumo vs immissione in rete</CardDescription>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                            formatter={(value, name) => [
                                `${value.toLocaleString('it-IT')} kWh`,
                                name === 'autoconsumo' ? 'Autoconsumo' : 'Immessa in rete'
                            ]}
                        />
                        <Legend 
                            formatter={(value) => value === 'autoconsumo' ? 'Autoconsumo' : 'Immessa in rete'}
                        />
                        <Area 
                            type="monotone" 
                            dataKey="autoconsumo" 
                            stackId="1"
                            stroke={COLORS.selfConsumption} 
                            fill={COLORS.selfConsumption}
                            fillOpacity={0.6}
                        />
                        <Area 
                            type="monotone" 
                            dataKey="immissione" 
                            stackId="1"
                            stroke={COLORS.gridExport} 
                            fill={COLORS.gridExport}
                            fillOpacity={0.6}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

/**
 * Grafico ammortamento e break-even
 */
export function AmortizationChart({ totalInvestment, annualSaving, years = 25 }) {
    const data = useMemo(() => {
        if (!totalInvestment || !annualSaving) return [];
        
        let cumulativeSavings = 0;
        const result = [];
        
        for (let year = 0; year <= years; year++) {
            result.push({
                anno: year,
                risparmio: Math.round(cumulativeSavings),
                investimento: Math.round(totalInvestment),
            });
            cumulativeSavings += annualSaving;
        }
        
        return result;
    }, [totalInvestment, annualSaving, years]);

    const breakEvenYear = useMemo(() => {
        if (!totalInvestment || !annualSaving || annualSaving <= 0) return null;
        return Math.ceil(totalInvestment / annualSaving);
    }, [totalInvestment, annualSaving]);

    if (data.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Ammortamento Investimento</CardTitle>
                <CardDescription>
                    Risparmio cumulativo vs investimento iniziale
                    {breakEvenYear && breakEvenYear <= 25 && (
                        <span className="ml-2 text-green-600 font-medium">
                            • Break-even: anno {breakEvenYear}
                        </span>
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                            dataKey="anno" 
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Anni', position: 'insideBottom', offset: -5, fontSize: 12 }}
                        />
                        <YAxis 
                            tick={{ fontSize: 12 }} 
                            tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                            formatter={(value, name) => [
                                `€${value.toLocaleString('it-IT')}`,
                                name === 'risparmio' ? 'Risparmio Cumulativo' : 'Investimento'
                            ]}
                            labelFormatter={(label) => `Anno ${label}`}
                        />
                        <Legend 
                            formatter={(value) => value === 'risparmio' ? 'Risparmio Cumulativo' : 'Investimento Iniziale'}
                        />
                        <Area 
                            type="monotone" 
                            dataKey="risparmio" 
                            stroke={COLORS.savings} 
                            fill={COLORS.savings}
                            fillOpacity={0.3}
                            strokeWidth={2}
                        />
                        <ReferenceLine 
                            y={totalInvestment} 
                            stroke={COLORS.investment} 
                            strokeDasharray="5 5"
                            strokeWidth={2}
                            label={{ 
                                value: 'Investimento', 
                                position: 'right',
                                fill: COLORS.investment,
                                fontSize: 12
                            }}
                        />
                        {breakEvenYear && breakEvenYear <= 25 && (
                            <ReferenceLine 
                                x={breakEvenYear} 
                                stroke="#22c55e" 
                                strokeDasharray="3 3"
                                label={{ 
                                    value: `Break-even`, 
                                    position: 'top',
                                    fill: '#22c55e',
                                    fontSize: 11
                                }}
                            />
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

/**
 * Grafico confronto costi energetici
 */
export function EnergyCostComparisonChart({ 
    annualConsumption, 
    annualSaving, 
    currentEnergyPrice = 0.25,
    annualPriceIncrease = 4,
    years = 20 
}) {
    const data = useMemo(() => {
        if (!annualConsumption) return [];
        
        const result = [];
        let price = currentEnergyPrice;
        let totalWithoutPV = 0;
        let totalWithPV = 0;
        
        for (let year = 1; year <= years; year++) {
            const costWithoutPV = annualConsumption * price;
            const costWithPV = Math.max(0, costWithoutPV - annualSaving);
            
            totalWithoutPV += costWithoutPV;
            totalWithPV += costWithPV;
            
            result.push({
                anno: year,
                senzaFV: Math.round(totalWithoutPV),
                conFV: Math.round(totalWithPV),
                risparmio: Math.round(totalWithoutPV - totalWithPV),
            });
            
            price *= (1 + annualPriceIncrease / 100);
        }
        
        return result;
    }, [annualConsumption, annualSaving, currentEnergyPrice, annualPriceIncrease, years]);

    if (data.length === 0) return null;

    const totalSavings = data[data.length - 1]?.risparmio || 0;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Costi Energetici Cumulativi</CardTitle>
                <CardDescription>
                    Confronto costi con e senza impianto fotovoltaico in {years} anni
                    <span className="ml-2 text-green-600 font-medium">
                        • Risparmio totale: €{totalSavings.toLocaleString('it-IT')}
                    </span>
                </CardDescription>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                            dataKey="anno" 
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Anni', position: 'insideBottom', offset: -5, fontSize: 12 }}
                        />
                        <YAxis 
                            tick={{ fontSize: 12 }} 
                            tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                            formatter={(value, name) => {
                                const labels = {
                                    senzaFV: 'Senza Fotovoltaico',
                                    conFV: 'Con Fotovoltaico',
                                    risparmio: 'Risparmio'
                                };
                                return [`€${value.toLocaleString('it-IT')}`, labels[name]];
                            }}
                            labelFormatter={(label) => `Anno ${label}`}
                        />
                        <Legend 
                            formatter={(value) => {
                                const labels = {
                                    senzaFV: 'Senza FV',
                                    conFV: 'Con FV',
                                };
                                return labels[value] || value;
                            }}
                        />
                        <Line 
                            type="monotone" 
                            dataKey="senzaFV" 
                            stroke={COLORS.cost} 
                            strokeWidth={2}
                            dot={false}
                        />
                        <Line 
                            type="monotone" 
                            dataKey="conFV" 
                            stroke={COLORS.savings} 
                            strokeWidth={2}
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

/**
 * Grafico ROI annuale
 */
export function ROIChart({ totalInvestment, annualSaving, years = 25 }) {
    const data = useMemo(() => {
        if (!totalInvestment || !annualSaving) return [];
        
        const result = [];
        let cumulative = 0;
        
        for (let year = 1; year <= years; year++) {
            cumulative += annualSaving;
            const roi = ((cumulative - totalInvestment) / totalInvestment) * 100;
            
            result.push({
                anno: year,
                roi: Math.round(roi),
            });
        }
        
        return result;
    }, [totalInvestment, annualSaving, years]);

    if (data.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Return on Investment (ROI)</CardTitle>
                <CardDescription>Rendimento cumulativo dell'investimento</CardDescription>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                            dataKey="anno" 
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                            tick={{ fontSize: 12 }} 
                            tickFormatter={(v) => `${v}%`}
                        />
                        <Tooltip 
                            formatter={(value) => [`${value}%`, 'ROI']}
                            labelFormatter={(label) => `Anno ${label}`}
                        />
                        <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                        <Area 
                            type="monotone" 
                            dataKey="roi" 
                            stroke={COLORS.roi} 
                            fill={COLORS.roi}
                            fillOpacity={0.3}
                            strokeWidth={2}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

export default {
    MonthlyProductionChart,
    EnergyFlowsChart,
    AmortizationChart,
    EnergyCostComparisonChart,
    ROIChart
};
